import React, { memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import type { FeeWithdrawal } from 'src/interfaces/feeWithdrawal'
import { getFeeWithdrawalReportLoadingIds } from 'src/selectors/feeWithdrawals'

interface Props {
  withdrawal: FeeWithdrawal
  onDownloadReport(withdrawal: FeeWithdrawal): void
}

/**
 * The grid's Report cell. It reads the in-flight state itself so the column definitions never
 * depend on it — a new `columns` array on every download would make the grid re-apply them and
 * drop the user's column widths.
 */
const FeeWithdrawalReportCellComponent = ({ withdrawal, onDownloadReport }: Props) => {
  const reportLoadingIds = useSelector(getFeeWithdrawalReportLoadingIds)

  const handleDownload = useCallback(
    (event: React.MouseEvent) => {
      // The row opens the details modal; this button only downloads.
      event.stopPropagation()
      onDownloadReport(withdrawal)
    },
    [onDownloadReport, withdrawal],
  )

  if (!withdrawal.hasReport) {
    return (
      <Typography variant="body2" css={tw`text-xs text-shades-500`}>
        No report
      </Typography>
    )
  }

  return (
    <Button
      size="tiny"
      color="grey"
      loading={Boolean(reportLoadingIds[withdrawal.id])}
      onClick={handleDownload}
      data-testid={`fee-withdrawal-download-${withdrawal.id}`}
    >
      Download
    </Button>
  )
}

export const FeeWithdrawalReportCell = memo(FeeWithdrawalReportCellComponent)
